import { useState } from 'react'

import type {
  CouponApplyResult,
  ThresholdCouponSummary,
} from 'src/Components/ScheduleSummary'
import type { ThresholdCouponBannerProps } from 'src/Components/ScheduleSummary/ThresholdCouponBanner'
import { useIsThresholdCouponTestVariant } from 'src/Hooks/Experiments/useThresholdCouponExperiment'
import {
  MINIMUM_SPEND_ERROR,
  ThresholdCouponState,
  UseThresholdCoupon,
} from 'src/Hooks/useThresholdCoupon'
import { useThresholdCouponAnalytics } from 'src/Hooks/useThresholdCouponAnalytics'

type BannerState = ThresholdCouponBannerProps['state']

const BANNER_STATE: Partial<Record<ThresholdCouponState, BannerState>> = {
  [ThresholdCouponState.Offered]: 'offered',
  [ThresholdCouponState.AttachedBelowThreshold]: 'attachedBelowThreshold',
  [ThresholdCouponState.Unlocked]: 'unlocked',
}

type UseThresholdCouponSummaryParams = {
  threshold: UseThresholdCoupon
  isHidden: boolean
  campaignId: number
  platformName: string
  totalDiscount: number
  formatAmount: (value: number) => string
  onApplyPromocode: (
    couponCode: string,
  ) => Promise<CouponApplyResult | undefined>
}

type UseThresholdCouponSummary = {
  thresholdCoupon: ThresholdCouponSummary
  onApplyPromocode: (
    couponCode: string,
  ) => Promise<CouponApplyResult | undefined>
}

const keepAttachedMinimumSpend = (
  result: CouponApplyResult | undefined,
): CouponApplyResult | undefined => {
  if (result?.coupon.error?.message === MINIMUM_SPEND_ERROR) {
    return { coupon: { error: null, isBonusCoupon: false } }
  }
  return result
}

const isBannerVisible = (
  bannerState: BannerState | undefined,
  isHidden: boolean,
  isDismissedOffer: boolean,
): boolean => !isHidden && !isDismissedOffer && bannerState !== void 0

type BuildBannerParams = {
  threshold: UseThresholdCoupon
  bannerState: BannerState
  percent: number | null
  totalDiscount: number
  formatAmount: (value: number) => string
  onDismiss: () => void
  analytics: ReturnType<typeof useThresholdCouponAnalytics>
}

const buildBanner = ({
  threshold,
  bannerState,
  percent,
  totalDiscount,
  formatAmount,
  onDismiss,
  analytics,
}: BuildBannerParams): ThresholdCouponBannerProps => ({
  state: bannerState,
  code: threshold.couponCode,
  threshold: formatAmount(threshold.threshold),
  raisePayable:
    threshold.raisePayable === null
      ? void 0
      : formatAmount(threshold.raisePayable),
  remaining: formatAmount(threshold.remaining),
  saved: formatAmount(totalDiscount),
  progress: threshold.progress,
  onApply: () => {
    analytics.trackOfferApplied()
    void threshold.onApplyOffer()
  },
  onRemove: () => {
    analytics.trackRemoved()
    void threshold.onRemoveCoupon()
  },
  onRaiseToThreshold: threshold.canRaise
    ? () => {
        analytics.trackRaised()
        void threshold.onRaise()
      }
    : void 0,
  onDismiss: () => {
    analytics.trackOfferDismissed()
    onDismiss()
  },
  percent,
})

export const useThresholdCouponSummary = ({
  threshold,
  isHidden,
  campaignId,
  platformName,
  totalDiscount,
  formatAmount,
  onApplyPromocode,
}: UseThresholdCouponSummaryParams): UseThresholdCouponSummary => {
  const [isOfferDismissed, setIsOfferDismissed] = useState(false)
  const isTestVariant = useIsThresholdCouponTestVariant(
    !isHidden && threshold.state !== ThresholdCouponState.None,
  )
  const isBlockHidden = isHidden || !isTestVariant
  const analytics = useThresholdCouponAnalytics({
    threshold,
    campaignId,
    platformName,
    isHidden: isBlockHidden,
  })

  const bannerState = BANNER_STATE[threshold.state]
  const percent = threshold.discountPercent
  const isDismissedOffer =
    threshold.state === ThresholdCouponState.Offered && isOfferDismissed
  const isVisible = isBannerVisible(
    bannerState,
    isBlockHidden,
    isDismissedOffer,
  )
  const isUnlocked =
    !isBlockHidden &&
    threshold.state === ThresholdCouponState.Unlocked &&
    totalDiscount > 0

  const banner =
    isVisible && bannerState !== void 0
      ? buildBanner({
          threshold,
          bannerState,
          percent,
          totalDiscount,
          formatAmount,
          analytics,
          onDismiss: () => setIsOfferDismissed(true),
        })
      : null

  const discountLine =
    isUnlocked && percent !== null
      ? {
          percent,
          code: threshold.couponCode,
          amount: formatAmount(totalDiscount),
        }
      : null

  return {
    thresholdCoupon: { banner, discountLine },
    onApplyPromocode: async (couponCode) =>
      keepAttachedMinimumSpend(await onApplyPromocode(couponCode)),
  }
}
